import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  componentName: string;
  isAuthorMode?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo, componentName: string) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName, onError } = this.props;

    if (onError) {
      onError(error, errorInfo, componentName);
    }

    if (process.env.NODE_ENV === 'development') {
      console.error(`[ErrorBoundary] Component "${componentName}" failed to render:`, error, errorInfo?.componentStack);
    }
  }

  public override render() {
    const { hasError, error } = this.state;
    const { children, componentName, isAuthorMode } = this.props;

    if (hasError) {
      if (isAuthorMode) {
        return (
          <div
            data-component-name={componentName}
            style={{
              padding: '16px',
              border: '2px dashed #d32f2f',
              backgroundColor: '#ffebee',
              color: '#d32f2f',
              fontSize: '14px',
            }}
          >
            <span role="img" aria-label="warning">
              ⚠️
            </span>{' '}
            {componentName} failed to render
            {process.env.NODE_ENV === 'development' && error?.message && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>{error.message}</div>
            )}
          </div>
        );
      }
      return <div data-component-name={componentName}></div>;
    }

    return children;
  }
}

export default ErrorBoundary;
